import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { ADD_TO_CART_TEXT_ANIMATION_DURATION } from '@theme/product-form';
import { ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

function alignLensWithVariant(purchaseOptions, productForm) {
  const picker = purchaseOptions?.matches('variant-picker.aim-lens-picker')
    ? purchaseOptions
    : purchaseOptions?.querySelector('variant-picker.aim-lens-picker');
  if (!picker) return;

  const variantJson = picker.querySelector('script[type="application/json"]')?.textContent;
  if (!variantJson) return;

  let variant;
  try {
    variant = JSON.parse(variantJson);
  } catch {
    return;
  }
  if (!variant) return;

  const formVariantId = productForm?.querySelector('[name="id"]')?.value;
  if (formVariantId && String(variant.id) !== formVariantId) return;

  const inputs = Array.from(picker.querySelectorAll('input[type="radio"][data-input-id]'));
  const position = Number.parseInt(inputs[0]?.dataset.inputId?.split('-')[0], 10);
  const selectedValue = variant.options?.[position - 1] ?? variant[`option${position}`];
  if (typeof selectedValue !== 'string') return;

  for (const input of inputs) {
    const isSelected = input.value === selectedValue;
    input.checked = isSelected;
    input.toggleAttribute('checked', isSelected);
    input.dataset.currentChecked = String(isSelected);
    input.dataset.previousChecked = 'false';
  }
  const current = picker.querySelector('[data-lens-current]');
  if (current) current.textContent = selectedValue;
}


export class QuickAddComponent extends Component {
  #abortController = null;
  static #busy = false;

  get productPageUrl() {
    const productCard = this.closest('product-card');
    const productLink = productCard?.getProductCardLink();
    if (!productLink?.href) return '';

    const url = new URL(productLink.href);
    if (url.searchParams.has('variant')) return url.toString();

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) url.searchParams.set('variant', selectedVariantId);
    return url.toString();
  }

  #getSelectedVariantId() {
    return this.closest('product-card')?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  handleClick = async (event) => {
    event.preventDefault();
    if (QuickAddComponent.#busy) return;
    const currentUrl = this.productPageUrl;
    if (!currentUrl) return;

    QuickAddComponent.#busy = true;
    this.setAttribute('aria-busy', 'true');
    this.setAttribute('stay-visible', '');
    const trigger = event.target instanceof Element
      ? event.target.closest('button')
      : this.querySelector('.quick-add__button--choose');
    const label = trigger?.querySelector('.add-to-cart-text__content');
    const originalLabel = label?.textContent;
    if (label) label.textContent = document.getElementById('quick-add-dialog')?.dataset.loadingLabel || 'Loading…';

    try {
      const stock = await this.#getStockChoice(currentUrl);
      if (!this.isConnected) return;
      const productUrl = new URL(currentUrl);
      if (stock?.directAdd) {
        if (label) label.textContent = document.getElementById('quick-add-dialog')?.dataset.addingLabel || 'Adding…';
        productUrl.searchParams.delete('option_values');
        productUrl.searchParams.set('variant', stock.variantId);
      }

      const html = await this.fetchProductPage(productUrl.toString());
      const productGrid = html?.querySelector('[data-product-grid-content]');
      if (!productGrid || !this.isConnected) return;

      if (stock?.directAdd) {
        const result = await this.#addFromCard(productGrid, stock, trigger, originalLabel);
        if (result?.submitted && !result.error) return;
        await this.updateQuickAddModal(productGrid.cloneNode(true), { syncCardVariant: false });
        this.#openQuickAddModal();
        if (result?.error) this.#showCartError(result.error);
        return;
      }
      await this.updateQuickAddModal(productGrid.cloneNode(true));
      this.#openQuickAddModal();
    } catch (error) {
      if (error.name !== 'AbortError') window.location.assign(currentUrl);
    } finally {
      if (label) label.textContent = originalLabel;
      this.removeAttribute('aria-busy');
      if (!document.querySelector('#quick-add-dialog dialog[open]')) this.removeAttribute('stay-visible');
      QuickAddComponent.#busy = false;
    }
  };

  async #addFromCard(productGrid, stock, trigger, originalLabel) {
    const source = productGrid.querySelector('product-form-component');
    const sourceForm = source?.querySelector('form');
    const nativeControl = sourceForm?.querySelector('add-to-cart-component');
    const sourceButton = nativeControl?.querySelector('[ref="addToCartButton"]');
    const cardComponent = this.querySelector('product-form-component');
    const cardForm = cardComponent?.querySelector('form');
    const cardId = cardForm?.querySelector('[name="id"]');
    if (!nativeControl || !sourceButton || !cardId || !trigger || !cardForm?.contains(trigger)) return null;
    if (typeof cardComponent.handleSubmit !== 'function') return null;
    if (sourceForm.querySelector('[name="id"]')?.value !== stock.variantId || sourceButton.disabled) return null;
    if (source.dataset.productId && source.dataset.productId !== stock.productId) return null;
    if (sourceForm.querySelector('[name^="properties["], [name="selling_plan"], input[type="file"]')) return null;
    if (Number(source.dataset.quantityDefault || 1) !== 1) return null;
    if (Number(cardForm.querySelector('[name="quantity"]')?.value || 1) !== 1) return null;
    if (!sourceForm.checkValidity() || !cardForm.checkValidity()) return null;

    const replacement = nativeControl.cloneNode(true);
    const button = replacement.querySelector('[ref="addToCartButton"]');
    button.className = trigger.className;
    button.removeAttribute('id');
    button.setAttribute('on:click', '/handleClick');
    button.type = 'submit';
    button.querySelector('.add-to-cart-text__content').textContent = originalLabel || 'Quick Add';
    const oldText = trigger.querySelector('.add-to-cart-text__content');
    if (oldText) button.querySelector('.add-to-cart-text__content').className = oldText.className;
    if (oldText?.classList.contains('is-visually-hidden-mobile')) {
      replacement.querySelectorAll('.add-to-cart-text--added > span:not(.svg-wrapper)').forEach((span) => {
        span.classList.add('is-visually-hidden-mobile');
      });
    }
    const oldId = cardId.value;
    const oldDisabled = cardId.disabled;
    cardId.disabled = false;
    cardId.value = stock.variantId;
    trigger.replaceWith(replacement);
    await new Promise((resolve) => requestAnimationFrame(resolve));

    let result;
    try {
      result = await new Promise((resolve) => {
        const controller = new AbortController();
        const started = performance.now();
        let finished = false;
        const finish = async (error = '') => {
          if (finished) return;
          finished = true;
          clearTimeout(timeout);
          controller.abort();
          if (!error) {
            const remaining = Math.max(0, ADD_TO_CART_TEXT_ANIMATION_DURATION + 40 - (performance.now() - started));
            await new Promise((done) => setTimeout(done, remaining));
          }
          resolve({ submitted: true, error });
        };
        const timeout = setTimeout(() => finish('The cart did not respond. Please check your cart before trying again.'), 20000);
        cardComponent.addEventListener(ThemeEvents.cartUpdate, (event) => {
          finish(event.detail?.data?.didError ? 'Unable to add this item. Please try again.' : '');
        }, { signal: controller.signal });
        cardComponent.addEventListener(ThemeEvents.cartError || 'cart:error', (event) => {
          const error = event.detail?.data?.description || event.detail?.data?.message || event.detail?.description || event.detail?.message;
          finish(typeof error === 'string' ? error : 'Unable to add this item. Please try again.');
        }, { signal: controller.signal });
        button.addEventListener('click', (event) => {
          if (button.dataset.aimSubmitted === 'true') {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }
          button.dataset.aimSubmitted = 'true';
        }, { capture: true });
        button.click();
      });
    } finally {
      if (replacement.isConnected) replacement.replaceWith(trigger);
      if (cardId.value === stock.variantId) {
        cardId.value = oldId;
        cardId.disabled = oldDisabled;
      }
    }
    return result;
  }

  #showCartError(message) {
    const content = document.getElementById('quick-add-modal-content');
    const productForm = content?.querySelector('product-form-component');
    if (!productForm) return;
    let error = productForm.querySelector('[data-aim-cart-error]');
    if (!error) {
      error = document.createElement('p');
      error.dataset.aimCartError = '';
      error.setAttribute('role', 'alert');
      productForm.prepend(error);
    }
    error.textContent = message;
  }

  async #getStockChoice(productPageUrl) {
    const url = new URL(productPageUrl);
    url.searchParams.delete('sections');
    url.searchParams.delete('page');
    url.searchParams.set('section_id', 'aim-quick-add-stock');
    try {
      const html = await this.fetchProductPage(url.toString());
      const json = html?.querySelector('[data-aim-quick-add-stock]')?.textContent;
      if (!json) return null;
      const stock = JSON.parse(json);
      if (stock.directAdd !== true || !/^\d+$/.test(stock.variantId) || !/^\d+$/.test(stock.productId)) {
        return null;
      }
      return stock;
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      return null;
    }
  }

  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);
    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => this.toggleAttribute('stay-visible', false),
      { once: true }
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();
    dialogComponent.querySelector('#quick-add-modal-content')?.scrollTo({ top: 0 });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (dialogComponent instanceof QuickAddDialog) dialogComponent.closeDialog();
  };

  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;

    try {
      const response = await fetch(productPageUrl, { signal: controller.signal, cache: 'no-store' });
      if (!response.ok) throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') return null;
      throw error;
    } finally {
      if (this.#abortController === controller) this.#abortController = null;
    }
  }

  #getPurchaseOptions(productGrid) {
    const details = productGrid.querySelector('.product-details') || productGrid;
    const lensWrapper = details.querySelector('.aim-lens-picker-wrap');
    const standardPicker = details.querySelector('variant-picker:not(.aim-lens-picker)');
    const lensPicker = lensWrapper?.querySelector('variant-picker.aim-lens-picker');

    if (!lensWrapper) return standardPicker || details.querySelector('variant-picker');
    if (!standardPicker) return lensWrapper;

    const lensInput = lensPicker?.querySelector('input[data-input-id]');
    const lensPosition = lensInput?.dataset.inputId?.split('-')[0];
    const standardOptions = Array.from(standardPicker.querySelectorAll('[data-option-value-id]'));
    const hasOtherOptions = !lensPosition || standardOptions.some((option) => {
      return option.dataset.inputId?.split('-')[0] !== lensPosition;
    });

    if (hasOtherOptions) {
      lensWrapper.remove();
      return standardPicker;
    }

    standardPicker.remove();
    return lensWrapper;
  }

  async updateQuickAddModal(productGrid, { syncCardVariant = true } = {}) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) return;

    const purchaseOptions = this.#getPurchaseOptions(productGrid);
    const productFormComponent = productGrid.querySelector('product-form-component');
    alignLensWithVariant(purchaseOptions, productFormComponent);

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');
      productHeader.appendChild(productTitle);
      if (productPrice) productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      if (purchaseOptions) productGrid.appendChild(purchaseOptions);
      if (productFormComponent) productGrid.appendChild(productFormComponent);
      productDetails?.remove();
    }

    morph(modalContent, productGrid);
    if (syncCardVariant) this.#syncVariantSelection(modalContent);
  }

  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const options = { signal: this.#abortController.signal };

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, options);
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink, options);
    this.addEventListener('change', this.#updateLensLabel, options);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, options);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateLensLabel = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    const picker = input.closest('variant-picker.aim-lens-picker');
    if (!picker || !this.contains(picker)) return;
    const current = picker.querySelector('[data-lens-current]');
    if (current) current.textContent = input.value;
  };

  #updateProductTitleLink = (event) => {
    const html = event.detail.data.html;
    const anchorElement = html?.querySelector('.view-product-title a');
    const viewMoreDetailsLink = this.querySelector('.view-product-title a');
    const mobileProductTitle = this.querySelector('.product-header a');

    if (anchorElement) {
      if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
      if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
    }

    const purchaseOptions = this.querySelector('.aim-lens-picker-wrap');
    if (purchaseOptions) {
      this.#alignUpdatedLens(purchaseOptions, html);
    }
  };

  #alignUpdatedLens(purchaseOptions, html) {
    if (!html) return;
    const updatedDetails = html.querySelector('.product-details') || html;
    const updatedWrapper = updatedDetails.querySelector('.aim-lens-picker-wrap');
    if (!updatedWrapper) {
      purchaseOptions.remove();
      return;
    }
    const replacement = updatedWrapper.cloneNode(true);
    alignLensWithVariant(replacement, updatedDetails.querySelector('product-form-component'));
    morph(purchaseOptions, replacement);
  }

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
